import React, { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { SQUARE_SANDBOX_APP_ID, SQUARE_SANDBOX_LOCATION_ID } from '../constants';
import type { TicketForPayment } from '../App'; 

interface SquarePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketDetails: TicketForPayment;
  onPaymentSuccess: (ticketId: string, nonce: string, cardBrand: string, last4: string) => void;
}

declare global {
  interface Window {
    Square: any; 
  }
}

const LoadingSpinner: React.FC = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-20">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#003794]"></div> {/* Primary Blue Spinner */}
  </div>
);

export const SquarePaymentModal: React.FC<SquarePaymentModalProps> = ({
  isOpen,
  onClose,
  ticketDetails,
  onPaymentSuccess,
}) => {
  const cardFormRef = useRef<HTMLDivElement>(null);
  const [card, setCard] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      if (card) {
        card.destroy();
        setCard(null);
      }
      return;
    }

    if (!window.Square) {
      setErrorMessage('Square SDK not loaded. Please refresh the page.');
      return;
    }
    
    const initializeCard = async () => {
      try {
        const payments = window.Square.payments(SQUARE_SANDBOX_APP_ID, SQUARE_SANDBOX_LOCATION_ID);
        const cardInstance = await payments.card();
        
        if (cardFormRef.current) {
          await cardInstance.attach('#card-container'); 
          setCard(cardInstance);
          setErrorMessage(null); 
        }
      } catch (e) {
        console.error('Error initializing Square Card payment:', e);
        setErrorMessage('Failed to initialize payment form. Please try again.');
      }
    };

    if (isOpen && !card && cardFormRef.current) {
      initializeCard();
    }
    
  }, [isOpen, card]);


  const handlePayment = async () => {
    if (!card) {
      setErrorMessage('Payment form not initialized.');
      return;
    }
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      const result = await card.tokenize();
      if (result.status === 'OK') {
        console.log('Nonce:', result.token);
        const cardDetails = result.details;
        onPaymentSuccess(ticketDetails.id, result.token, cardDetails?.card?.brand || 'Unknown', cardDetails?.card?.last4 || '0000');
      } else {
        let message = `Tokenization failed with status: ${result.status}`;
        if (result.errors) {
          message += ` - ${result.errors.map((error: any) => error.message).join(' ')}`;
        }
        setErrorMessage(message);
        console.error('Square Tokenization Error:', result.errors);
      }
    } catch (e) {
      console.error('Error processing payment:', e);
      setErrorMessage('An unexpected error occurred during payment.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[100]"
      aria-labelledby="payment-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md relative">
        {isProcessing && <LoadingSpinner />}
        <div className="flex justify-between items-center mb-4">
          <h2 id="payment-modal-title" className="text-xl font-semibold text-slate-800">
            Process Payment for Ticket {ticketDetails.id}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close modal"
            disabled={isProcessing}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-slate-700">Customer: <span className="font-medium">{ticketDetails.customerName}</span></p>
          <p className="text-lg font-semibold text-slate-800">
            Amount Due: ${ticketDetails.amountDue.toFixed(2)}
          </p>
        </div>

        <div id="card-container" ref={cardFormRef} className="mb-4 p-3 border border-slate-300 rounded-md min-h-[50px]">
        </div>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-300 rounded-md text-sm">
            {errorMessage}
          </div>
        )}
        
        <p className="text-xs text-slate-500 mb-4">
            Powered by Square. This is a sandbox environment for testing.
        </p>

        <div className="border-t border-slate-200 pt-4 flex justify-end space-x-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handlePayment}
            disabled={isProcessing || !card}
          >
            {isProcessing ? 'Processing...' : `Pay $${ticketDetails.amountDue.toFixed(2)}`}
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">
          Reminder: Payment processing is simulated on the frontend. Real transactions require a backend.
        </p>
      </div>
    </div>
  );
};